import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { CfrClientModel, CfrStatusEnum } from "../../../../../models/cfr/cfr.models";
import { ChildComponent } from "../../child-component";
import { LookupValue, LookupValueRequest } from "../../../../../models/lookups/lookup.models";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { I18N } from "aurelia-i18n";
import { Router } from "aurelia-router";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { Claims } from "../../../../../constants/authentication/claims";
import * as moment from "moment";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import { LinkedIncidentModel, LinkedPscInspectionModel } from "../../../../../models/fbrs/fbr.models";
import { CfrService } from "../../../../../services/cfr/cfr.service";
import { EntityTypeConfigurationModel, EntityTypeConfigurationHelper } from "../../../../../models/basics/basics.models";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { FbrService } from "../../../../../services/fbr/fbr.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { Assignment } from "../../../../components/assignment/assignment";
import { Reflection } from "../../../../../utilities/reflection";

@autoinject
@transient()
@useView('views/pages/modules/cfr/tabs/cfr-overview.html')
export class CfrOverview extends ChildComponent<CfrClientModel> {
    private cfrStatuses: LookupValue[];
    public isRightShipUser: boolean;
    reassignment: CfrStatusEnum = CfrStatusEnum.CfrReassignment;
    canEditFbrOutcome: boolean;
    assignment: Assignment;
    assignToMe: any;

    constructor(private cfrService: CfrService, private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        notificationsService: NotificationClientService, lookupClientService: LookupClientService, private userClientService: UserClientService,
        private userClaimsService: UserClaimsService, private notificationClientService: NotificationClientService,
        i18N: I18N, private router: Router, private fbrService: FbrService, private dialogClientService: DialogClientService) {
        super(notificationsService, i18N);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.CfrStatuses;
        lookupClientService.getLookup(request).then(x => {
            this.cfrStatuses = x.activeValues;
        });
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canEditFbrOutcome = this.userClaimsService.hasPermission(Claims.CanEditFbrOutcome);
        this.pscInspectionEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Pscs"]);
        this.incidentEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Incidents"]);
    }

    activate(model: { item: CfrClientModel, assignment: Assignment }) {
        if(model) {
            super.activate(model.item);
            this.assignment = model.assignment;
        }
    }

    @computedFrom(Reflection.getPropertyPath<CfrOverview>(z => z.item),
        Reflection.getPropertyPath<CfrOverview>(z => z.item.cfrStatusId))
    get isClosed(): boolean {
        return this.item && (this.item.cfrStatusId == CfrStatusEnum.Closed);
    }

    navigateToFbr(fbrId: number) {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Fbr, { id: fbrId });
    }

    get awaitingResponse() {
        return this.item && (this.item.cfrStatusId == CfrStatusEnum.AwaitingResponse);
    }

    get fbrStatus() {
        if (this.cfrStatuses && this.item)
            return this.cfrStatuses.filter(x => x.id == this.item.cfrStatusId)[0];

        return null;
    }

    @computedFrom("item", "item.built")
    get built() {
        const date = moment(this.item.built);
        const yearAge = moment().diff(date, 'year', true).toFixed(1) + "y/o" + " (" + date.year() + ")";
        return yearAge;
    }

    @computedFrom("item", "item.cfrStatusHistory", "item.cfrStatusHistory.length", "item.cfrStatusId")
    get lastCfrStatusHistory() {
        return this.item.cfrStatusHistory[0];
    }

    navigateToVessel() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselDetails, { id: this.item.vesselId });
    }

    @computedFrom(Reflection.getPropertyPath<CfrOverview>(z => z.item),
        Reflection.getPropertyPath<CfrOverview>(z => z.item.dateRequested))
    get date() {
        if (this.item && this.item.dateRequested)
            return this.item.dateRequested;

        return null;
    }

    // LINKED INCIDENTS
    incidentEntityTypes: EntityTypeConfigurationModel[];
    incidentLinkingVisible: boolean = false;
    incidentIds: number[] = [];

    newIncident() {
        if (this.item.assignedUserId == this.userClientService.currentUser.id) {
            this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddIncident, {
                cfrId: this.item.cfrId,
                id: 0,
                vesselId: this.item.vesselId,
                internationalMaritimeOrganizationNumber:
                    this.item.internationalMaritimeOrganizationNumber ? this.item.internationalMaritimeOrganizationNumber : '0',
                vesselName: this.item.vesselName
            });
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
                name: "Condition for Review",
                submit: this.assignToMe.bind(this),
                callback: this.newIncident.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    async linkToIncident() {
        if (this.item.assignedUserId == this.userClientService.currentUser.id) {
            try {
                const promise: Promise<LinkedIncidentModel[]> = this.cfrService.linkToIncident(this.item.cfrId, this.incidentIds);
                const inc = await promise;

                this.incidentLinkingVisible = false;
                for (const i of inc)
                    this.item.linkedIncidents.push(i);
                this.incidentIds = [];
            } catch (e) {
                this.notificationClientService.show("Failed to link incident", "", NotificationType.Error);
                throw e;
            }
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
                name: "Condition for Review",
                submit: this.assignToMe.bind(this),
                callback: this.linkToIncident.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    async unlinkIncident(incidentId: number) {
        if (this.item.assignedUserId == this.userClientService.currentUser.id) {
            try {
                const promise: Promise<void> = this.cfrService.unlinkIncident(this.item.cfrId, incidentId);
                await promise;

                const incs = this.item.linkedIncidents.filter(x => x.incidentId == incidentId);

                for (const i of incs) {
                    const index = this.item.linkedIncidents.indexOf(i);
                    if (index >= 0)
                        this.item.linkedIncidents.splice(index, 1);
                }

            } catch (e) {
                this.notificationClientService.show("Failed to un-link incident", "", NotificationType.Error);
                throw e;
            }
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
                name: "Condition for Review",
                submit: this.assignToMe.bind(this),
                callback: () => {
                    this.unlinkIncident(incidentId);
                },
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    showIncidentLinking() {
        if (this.item.assignedUserId == this.userClientService.currentUser.id) {
            this.incidentLinkingVisible = true;
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
                name: "Condition for Review",
                submit: this.assignToMe.bind(this),
                callback: this.showIncidentLinking.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    cancelIncidentLinking() {
        this.incidentLinkingVisible = false;
    }

    onCustomerSelected(currentItem: CfrClientModel, customer) {
        currentItem.customerId = customer ? customer.id : null;
    }


    // LINKED PSC INSPECTIONS
    pscInspectionEntityTypes: EntityTypeConfigurationModel[];
    pscInspectionLinkingVisible: boolean = false;
    pscInspectionIds: number[] = [];

    newPscInspection() {
        if (this.item.assignedUserId == this.userClientService.currentUser.id) {
            this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddPsc, {
                cfrId: this.item.cfrId,
                id: 0,
                vesselId: this.item.vesselId,
                internationalMaritimeOrganizationNumber:
                    this.item.internationalMaritimeOrganizationNumber ? this.item.internationalMaritimeOrganizationNumber : '0',
                vesselName: this.item.vesselName
            });
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
                name: "Condition for Review",
                submit: this.assignToMe.bind(this),
                callback: this.newPscInspection.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    async linkToPscInspection() {
        if (this.item.assignedUserId == this.userClientService.currentUser.id) {
            try {
                const promise: Promise<LinkedPscInspectionModel[]> = this.cfrService.linkToPscInspection(this.item.cfrId, this.pscInspectionIds);
                const inc = await promise;

                this.pscInspectionLinkingVisible = false;
                for (const i of inc)
                    this.item.linkedPscInspections.push(i);
                this.pscInspectionIds = [];
            } catch (e) {
                this.notificationClientService.show("Failed to link psc inspection", "", NotificationType.Error);
                throw e;
            }
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
                name: "Condition for Review",
                submit: this.assignToMe.bind(this),
                callback: this.linkToPscInspection.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    async unlinkPscInspection(pscInspectionId: number) {
        if (this.item.assignedUserId == this.userClientService.currentUser.id) {
            try {
                const promise: Promise<void> = this.cfrService.unlinkPscInspection(this.item.cfrId, pscInspectionId);
                await promise;

                const pscs = this.item.linkedPscInspections.filter(x => x.pscInspectionId == pscInspectionId);

                for (const i of pscs) {
                    const index = this.item.linkedPscInspections.indexOf(i);
                    if (index >= 0)
                        this.item.linkedPscInspections.splice(index, 1);
                }

            } catch (e) {
                this.notificationClientService.show("Failed to un-link psc inspection", "", NotificationType.Error);
                throw e;
            }
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
                name: "Condition for Review",
                submit: this.assignToMe.bind(this),
                callback: () => {
                    this.unlinkPscInspection(pscInspectionId);
                },
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    showPscInspectionLinking() {
        if (this.item.assignedUserId == this.userClientService.currentUser.id) {
            this.pscInspectionLinkingVisible = true;
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
                name: "Condition for Review",
                submit: this.assignToMe.bind(this),
                callback: this.showPscInspectionLinking.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    cancelPscInspectionLinking() {
        this.pscInspectionLinkingVisible = false;
    }

    get isCfrClosed() {
        return this.item && (this.item.cfrStatusId == CfrStatusEnum.Closed);
    }

    saveComments() {
        return new Promise<void>((resolve, reject) => {
            this.fbrService.saveComments(this.item.fbrId, [{
                fbrItemId: this.item.fbrItemId,
                comment: this.item.comments,
                flagForAllVets: this.item.flagForAllVets,
                flagForCustomer: this.item.flagForCustomer
            }], this.item.comments).then(() => {
                this.item.commentDateModified = new Date();
                this.notificationClientService.show("FBR Item Comment", "FBR Item Comment saved successfully", NotificationType.Success);
                resolve();
            }, () => {
                this.notificationClientService.show("FBR Item Comment", "FBR Item Comment failed to save", NotificationType.Error);
                reject();
            });
        });
    }

    flagForCustomer() {
        return this.item && this.item.flagForCustomer;
    }
}